/**
 * FinancialValue — Renders monetary values per ClaimGenius design rules.
 *
 * Design Spec: docs/plans/2026-06-25-redesign-design-spec.md §1, §2
 * Design Law:  docs/plans/2026-07-24-k3-black-orange-design-law.md §3, §4
 *
 * STRICT SEMANTICS (black+orange design law):
 * - DM Mono, tabular-nums, right-aligned — no numeral ever renders in proportional Outfit
 * - Currency: $1,234,567 (no decimals for large amounts)
 * - Negative: parentheses ($12,345) — muted gray, NOT red by default. Routine
 *   negative ≠ alert. Pass `adverse` to render crimson for a genuinely adverse
 *   figure (over-limit exposure, sublimit breach, shortfall) per the design law.
 * - value === 0            → em-dash "—" with title="Verified zero" (the engine ran and produced zero)
 * - value == null/undefined → ghost "NOT RUN" chip (never an em-dash, never empty —
 *   absence of a run must not be confusable with a verified zero)
 * - Strings are normalized (strip $/commas/parens); abbreviations like "1.2M" are
 *   rendered raw with data-warn="unnormalized" rather than inventing a number.
 * - Never abbreviate on output ($1.2M → always $1,200,000)
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { formatFinancial } from '../../utils/financialFormat';

interface FinancialValueProps {
  /** Raw numeric value, or pre-formatted string from backend */
  value: number | string | null | undefined;
  /** Show as percentage instead of currency */
  percent?: boolean;
  /** Additional CSS classes (merged last — callers may override size/color) */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * NEW (backward-compatible): design-law alias for `percent`.
   * `kind="percent"` ≡ `percent`; `kind="currency"` (default) is a no-op.
   */
  kind?: 'currency' | 'percent';
  /**
   * NEW (backward-compatible): contra/deduction row (e.g. "Less Threshold").
   * Renders the magnitude in parentheses per accounting convention — never red.
   */
  contra?: boolean;
  /**
   * NEW (backward-compatible): compact inline figure for formula columns.
   * Skips the size variant and default color so the figure inherits the
   * surrounding cell's font-size/color (e.g. 11px muted mono formula column).
   * Mono + tabular-nums are still enforced.
   */
  compact?: boolean;
  /**
   * NEW (backward-compatible, default false): penny precision (2 decimals) for
   * per-transaction audit surfaces ONLY — ledger rows, transaction tables — where
   * the cent must reconcile. Whole-dollar is the default and the law for every
   * computed/summary figure. Affects only a nonzero currency value; zero→em-dash,
   * null→NOT RUN, negative→parens, and percent mode are unchanged.
   */
  cents?: boolean;
  /**
   * NEW (backward-compatible, default false): mark this figure as ADVERSE —
   * renders it in crimson (`--color-blocker`) per the design law. Use ONLY for
   * genuinely adverse figures the case's truth turns on (over-limit exposure,
   * sublimit breach, shortfall), NEVER for routine negatives (a normal expense
   * or contra row stays muted-gray parens — "routine negative ≠ alert"). When
   * true, crimson takes precedence over compact-inherit and the parens/positive
   * neutral colors. Does not apply to the zero em-dash or the NOT RUN chip.
   */
  adverse?: boolean;
  /**
   * NEW (WP14, optional): why the figure is missing (backend `not_computed.reason`). Sets the
   * chip's `title` / `data-reason` and adds a ghost sub-line. No effect on a present value.
   */
  reason?: string;
  /** NEW (WP14, optional): override the NOT RUN chip text (e.g. "Not computed"). Default unchanged. */
  notRunLabel?: string;
  /** NEW (WP14, optional): override the verified-zero text (e.g. "$0.00"). Default unchanged ("—"). */
  zeroLabel?: string;
}

const sizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-2xl font-bold',
};

/** Mono + tabular figures enforced at the style level, belt-and-suspenders with classes. */
const monoStyle: React.CSSProperties = {
  fontFamily: 'DM Mono, ui-monospace, monospace',
  fontVariantNumeric: 'tabular-nums',
};

export function FinancialValue({
  value,
  percent = false,
  className,
  size = 'md',
  kind,
  contra = false,
  compact = false,
  cents = false,
  adverse = false,
  reason,
  notRunLabel,
  zeroLabel,
}: FinancialValueProps) {
  const asPercent = percent || kind === 'percent';
  const sizeClass = compact ? undefined : sizeClasses[size];

  // ── Number→string rule lives in ONE place (utils/financialFormat) ─────────
  // The component only maps the resolved state to presentation (mono/align/color/
  // chip); the formatting rule itself is shared with the `formatCurrency` string
  // helper so the two can never drift.
  const result = formatFinancial(value, { percent: asPercent, contra, cents });

  // Unnormalized backend string ("1.2M", non-numeric) → render raw, visibly flagged.
  if (result.state === 'unnormalized') {
    return (
      <span
        className={cn(
          'font-mono tabular-nums text-right',
          sizeClass,
          'text-[color:var(--color-text-muted)]',
          className
        )}
        style={monoStyle}
        data-warn="unnormalized"
        title="Unnormalized value from backend — displayed as received; not usable for arithmetic"
      >
        {result.text}
      </span>
    );
  }

  // null/undefined → ghost NOT RUN chip (never a dash, never empty).
  if (result.state === 'notRun') {
    const chip = (
      <span
        className={cn(
          'inline-flex items-center justify-end rounded-full border px-1.5 py-px',
          'font-mono uppercase tracking-[0.06em] text-[10px] leading-4 opacity-60',
          'text-[color:var(--color-text-ghost)] border-[var(--color-border-sub)]',
          reason ? undefined : className
        )}
        style={monoStyle}
        data-state="not-run"
        data-reason={reason}
        title={reason ?? 'Not run — no engine value for this figure'}
      >
        {notRunLabel ?? result.text}
      </span>
    );
    if (!reason) return chip;
    return (
      <span className={cn('inline-flex flex-col items-end gap-0.5', className)}>
        {chip}
        <span
          className="text-[11px] leading-3 text-[color:var(--color-text-muted)]"
          data-testid="not-computed-reason"
        >
          {reason}
        </span>
      </span>
    );
  }

  // Verified zero → em-dash (the engine ran; the answer is zero).
  if (result.state === 'zero') {
    return (
      <span
        className={cn(
          'font-mono tabular-nums text-right',
          sizeClass,
          'text-[color:var(--color-text-ghost)]',
          className
        )}
        style={monoStyle}
        title="Verified zero"
      >
        {zeroLabel ?? result.text}
      </span>
    );
  }

  // ── Value ─────────────────────────────────────────────────────────────────
  const formatted = result.text;                // already parenthesized if negative/contra
  const inParens = result.isNegative || contra; // for the color branch only

  return (
    <span
      className={cn(
        'font-mono tabular-nums text-right',
        sizeClass,
        // Adverse figures render crimson (design law) — overrides compact-inherit
        // and the neutral parens/positive colors. Routine negatives are NOT adverse.
        adverse
          ? 'text-[color:var(--color-blocker)]'
          : // Parenthesized (negative/contra) figures: muted gray — NEVER red.
            !compact &&
              (inParens
                ? 'text-[color:var(--color-text-muted)]'
                : 'text-[color:var(--color-text-secondary)]'),
        className
      )}
      style={monoStyle}
    >
      {formatted}
    </span>
  );
}

export default FinancialValue;
